import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormField from '../../../_components/Authorization/FormField';

/**
 * Custom filter for Autocomplete with smart sorting:
 * 1. Exact matches (case-insensitive)
 * 2. Prefix matches (starts with search term)
 * 3. Substring matches (contains search term)
 */
const createFilterOptions = () => (options, { inputValue }) => {
  if (!inputValue) {
    return options;
  }

  const searchTerm = inputValue.toLowerCase();

  // Categorize options
  const exactMatches = [];
  const prefixMatches = [];
  const substringMatches = [];

  options.forEach((option) => {
    const optionLabel = (option.name || option.title || option.address || '').toLowerCase();

    if (optionLabel === searchTerm) {
      exactMatches.push(option);
    } else if (optionLabel.startsWith(searchTerm)) {
      prefixMatches.push(option);
    } else if (optionLabel.includes(searchTerm)) {
      substringMatches.push(option);
    }
  });

  return [...exactMatches, ...prefixMatches, ...substringMatches];
};

const useStyles = makeStyles((theme) => ({
  block: {
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  blockTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.black,
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    fontSize: '12px',
    color: theme.palette.error.main,
    marginTop: '4px',
  },
  formControl: {
    minWidth: '100%',
    marginBottom: theme.spacing(2),
  },
}));

/**
 * DeliveryBlock - Блок "Доставка"
 * Поля: Тип доставки, Місто, Адреса/Відділення
 */
const DeliveryBlock = ({
  formState,
  errors,
  handleSelectChange,
  handleDepartmentsChange,
  listDelivery,
  listCities,
  listDepartments,
  onDeliveryTypeChange,
  onCityChange,
  isLoadingDepartments,
  fixedDeliveryType,
  fixedDeliveryTypeName,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.block}>
      <Typography className={classes.blockTitle}>
        🚚 {t('delivery') || 'Доставка'}
      </Typography>

      <Grid container spacing={2}>
        {/* Тип доставки */}
        <Grid item xs={12}>
          {fixedDeliveryType ? (
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('deliveryType') || 'Тип доставки'}
              </Typography>
              <Typography>{fixedDeliveryTypeName}</Typography>
            </Box>
          ) : (
            <FormControl className={classes.formControl} error={!!errors?.deliveryType}>
              <InputLabel className="form__field-label">{t('deliveryType') || 'Тип доставки'}</InputLabel>
              <Select
                className="form__field-input"
                name="deliveryType"
                value={formState.values.deliveryType || ''}
                onChange={(e) => {
                  handleSelectChange(e);
                  onDeliveryTypeChange(e.target.value);
                }}
                label={t('deliveryType') || 'Тип доставки'}
                required
              >
                <MenuItem value="">
                  <em>{t('selectDeliveryType') || 'Оберіть тип доставки'}</em>
                </MenuItem>
                {listDelivery?.map((item) => (
                  <MenuItem key={item.id} value={item.code || item.id}>
                    {item.name || item.title}
                  </MenuItem>
                ))}
              </Select>
              {errors?.deliveryType && (
                <Typography className={classes.errorText}>
                  {errors.deliveryType[0]}
                </Typography>
              )}
            </FormControl>
          )}
        </Grid>

        {/* Місто */}
        {formState.values.deliveryType && (
          <Grid item xs={12}>
            <Autocomplete
              id="autocomplete-delivery-city"
              size="small"
              options={listCities || []}
              getOptionLabel={(option) => option.name || option.title || ''}
              filterOptions={createFilterOptions()}
              value={
                (listCities || []).find((it) => String(it.id) === String(formState.values.deliveryCity)) || null
              }
              onChange={(event, value) => {
                const id = value ? value.id : '';
                // update form state via provided handler
                handleSelectChange({ target: { name: 'deliveryCity', value: id } });
                onCityChange(id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('city') || 'Місто'}
                  variant="outlined"
                  fullWidth
                  error={!!errors?.deliveryCity}
                  helperText={errors?.deliveryCity ? errors.deliveryCity[0] : ''}
                  InputProps={{
                    ...params.InputProps,
                    className: 'form__field-input',
                  }}
                  InputLabelProps={{ 
                    ...params.InputLabelProps,
                    className: 'form__field-label',
                  }}
                />
              )}
            />
          </Grid>
        )}

        {/* Адреса або Відділення */}
        {formState.values.deliveryCity && (
          <>
            {/* Якщо є відділення - показати селект */}
            {listDepartments && listDepartments.length > 0 && (
              <Grid item xs={12}>
                <Autocomplete
                  id="autocomplete-delivery-department"
                  size="small"
                  options={listDepartments || []}
                  getOptionLabel={(option) => option.name || option.address || ''}
                  filterOptions={createFilterOptions()}
                  value={
                    (listDepartments || []).find(
                      (it) => String(it.id) === String(formState.values.deliveryDepartment),
                    ) || null
                  }
                  onChange={(event, value) => {
                    const id = value ? value.id : '';
                    handleDepartmentsChange({ target: { name: 'deliveryDepartment', value: id } });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('department') || 'Відділення'}
                      variant="outlined"
                      fullWidth
                      error={!!errors?.deliveryDepartment}
                      helperText={errors?.deliveryDepartment ? errors.deliveryDepartment[0] : ''}
                      InputProps={{
                        ...params.InputProps,
                        className: 'form__field-input',
                      }}
                      InputLabelProps={{ 
                        ...params.InputLabelProps,
                        className: 'form__field-label',
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Текстова адреса */}
            {(!listDepartments || listDepartments.length === 0) && (
              <Grid item xs={12}>
                <FormField
                  handleChange={handleSelectChange}
                  name="addressDelivery"
                  title={t('address') || 'Адреса доставки'}
                  type="textarea"
                  formState={formState}
                  hasErrorText={errors?.addressDelivery ? errors.addressDelivery[0] : ''}
                  placeholder={t('enterDeliveryAddress') || 'Введіть адресу доставки'}
                  required={false}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

export default DeliveryBlock;
